import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { essentialTranslations } from "@/content/essentialTranslations";
import { getLanguageFromPath } from "@/lib/i18n";
import { getLanguageDictionary, loadLanguage, type Language } from "@/lib/translations";
import { recoverChunkLoadError } from "@/lib/chunkRecovery";
import { useCurrencyOrDefault } from "@/contexts/CurrencyContext";
import { fillAccountPlanPrices } from "@/lib/currency";

export type { Language } from "@/lib/translations";

interface LanguageContextType {
  language: Language;
  /** Set and persist an explicit language choice after its dictionary is ready. */
  setLanguage: (lang: Language) => void;
  /** Synchronize with routing or an account preference without changing persistence. */
  setLanguageFromUrl: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);
const LANG_STORAGE_KEY = "fluxabee-lang";
const useBrowserLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;

const persistLanguage = (language: Language) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(LANG_STORAGE_KEY, language);
  } catch { /* In-memory language selection also works with blocked storage. */ }
  try {
    const secure = window.location.protocol === "https:" ? "; secure" : "";
    document.cookie = `${LANG_STORAGE_KEY}=${language}; path=/; max-age=31536000; samesite=lax${secure}`;
  } catch { /* Cookie restrictions do not block the selected language. */ }
};

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const [language, setLanguageState] = useState<Language>(() =>
    getLanguageFromPath(location.pathname) ?? "en",
  );
  const requestVersion = useRef(0);

  const requestLanguage = useCallback((next: Language, persist: boolean) => {
    const version = ++requestVersion.current;
    const apply = () => {
      if (version !== requestVersion.current) return;
      setLanguageState(next);
      if (persist) persistLanguage(next);
    };
    if (getLanguageDictionary(next)) {
      apply();
      return;
    }
    void loadLanguage(next).then(apply).catch((error: unknown) => {
      if (version === requestVersion.current) recoverChunkLoadError(error);
    });
  }, []);
  const setLanguage = useCallback((next: Language) => requestLanguage(next, true), [requestLanguage]);
  const setLanguageFromUrl = useCallback((next: Language) => requestLanguage(next, false), [requestLanguage]);

  // A language navigation preloads its dictionary. Commit its language before paint;
  // account routes instead retain the saved preference supplied by their guard.
  useBrowserLayoutEffect(() => {
    if (/^\/account(?:\/|$)/.test(location.pathname)) return;
    const next = getLanguageFromPath(location.pathname) ?? "en";
    if (next !== language) setLanguageFromUrl(next);
  }, [location.pathname, language, setLanguageFromUrl]);

  useEffect(() => () => { requestVersion.current += 1; }, []);
  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const dictionary = getLanguageDictionary(language);
  const currency = useCurrencyOrDefault();
  // Plan price tokens ({price.basic} etc.) follow the selected currency.
  const t = (key: string): string => fillAccountPlanPrices(dictionary?.[key] ?? essentialTranslations[language][key] ?? key, currency, language);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, setLanguageFromUrl, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) throw new Error("useLanguage must be used within LanguageProvider");
  return context;
};
